import React, { useState, useCallback } from "react";
import { message } from "antd";
import { useDropzone } from "react-dropzone";
import { IconUpload } from "@tabler/icons";
import pdfIcon from "../../../static/icons/pdf.png";
import { isImageFile, isPdfFile } from "../../../helpers/utilities";
import { postUploadConsultationFiles } from "../../../helpers/api";
import { CONSULTATION_ATTACHMENT_LIMIT } from "../../../helpers/config";

const UploadFiles = ({ onUpload, consultationId }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const maxFiles = CONSULTATION_ATTACHMENT_LIMIT;
  const disabled = uploadedFiles.length >= maxFiles;

  const handleFileUpload = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        message.error("Some files were rejected");
        return;
      }

      const currentCount = uploadedFiles.length;

      if (currentCount + acceptedFiles.length > maxFiles) {
        message.warning(
          `You can only upload a maximum of ${maxFiles} files. You currently have ${currentCount} files.`
        );
        return;
      }

      const newFiles = acceptedFiles.map((file) => ({
        fileName: file.name,
        url: URL.createObjectURL(file),
        file: file,
      }));
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    },
    [uploadedFiles.length, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    maxSize: 10 * 1024 * 1024, // 10MB
    accept: {
      "image/*": [],
      "application/pdf": [],
    },
    multiple: true,
    disabled: uploadedFiles.length >= maxFiles,
  });

  const showHover = !disabled && isHovered && !isDragActive;

  const handleUploadSubmit = async () => {
    if (uploadedFiles.length === 0) {
      message.warning("Please select files to upload");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      uploadedFiles.forEach((fileObj) => {
        formData.append("files", fileObj.file);
      });

      postUploadConsultationFiles(consultationId, formData)
        .then((res) => {
          if (res.status === 201) {
            message.success("Files uploaded successfully!");
            onUpload(uploadedFiles);
            setUploadedFiles([]);
            return;
          }

          message.error(
            res?.response?.data?.message || "Failed to upload files"
          );
        })
        .finally(() => {
          setIsUploading(false);
        });
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload files");
      setIsUploading(false);
    }
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="rounded p-4 text-center cursor-pointer"
        style={{
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderWidth: "2px",
          borderStyle: "dashed",
          borderColor: disabled
            ? "var(--bs-gray-300)"
            : isDragActive
              ? "var(--bs-primary)"
              : showHover
                ? "var(--bs-primary)"
                : "#6c757d",
          backgroundColor: disabled
            ? "var(--bs-light)"
            : isDragActive
              ? "rgba(13, 110, 253, 0.08)"
              : showHover
                ? "rgba(13, 110, 253, 0.05)"
                : "transparent",
          boxShadow: showHover ? "0 0 0 1px var(--bs-primary)" : "none",
          transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
        }}>
        <input {...getInputProps()} />
        <div>
          <IconUpload
            size={48}
            className={`mb-3 ${uploadedFiles.length >= maxFiles
              ? "text-muted opacity-50"
              : isDragActive
                ? "text-primary"
                : "text-muted"
              }`}
          />
          <p className="mb-2">
            <strong>
              {uploadedFiles.length >= maxFiles
                ? `Maximum files reached (${maxFiles}/${maxFiles})`
                : isDragActive
                  ? "Drop files here"
                  : "Drag files here or click to select"}
            </strong>
          </p>
          <p className="text-muted small">
            Upload medical reports, lab results, images, and documents
          </p>
          <p className="text-muted small">
            Max file size: 10MB | Supported: Images and PDF | Max files:{" "}
            {maxFiles}
          </p>
          {uploadedFiles.length >= maxFiles && (
            <p className="text-warning small mt-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Maximum file limit reached. Remove some files to add more.
            </p>
          )}
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="mt-4">
          <h6>New Files to Upload ({uploadedFiles.length})</h6>
          <div className="row">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="col-md-6 mb-2">
                <div className="border rounded p-2 d-flex align-items-center">
                  <div className="me-2">
                    {isImageFile(file.fileName) ? (
                      <img
                        src={file.url}
                        alt={file.fileName}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    ) : isPdfFile(file.fileName) ? (
                      <img
                        src={pdfIcon}
                        alt="PDF"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "4px",
                        }}>
                        <i
                          className="bi bi-file-earmark"
                          style={{
                            fontSize: "1.5rem",
                            color: "#6c757d",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <p className="mb-0 small">
                      {file.fileName.length > 20
                        ? file.fileName.substring(0, 20) + "..."
                        : file.fileName}
                    </p>
                    <p className="text-muted small mb-0">
                      {(file.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={() => removeUploadedFile(index)}
                    title="Remove file">
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-end">
            <button
              className={`button btn app-btn btn-prim ${isUploading && " is-loading"
                }`}
              onClick={handleUploadSubmit}
              disabled={uploadedFiles.length === 0 || isUploading}>
              <IconUpload size={18} className="me-2" />
              {isUploading ? "Uploading..." : "Upload Files"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadFiles;
